import React from 'react';
import * as T from '../time.js';
import { api } from '../api.js';
import { mapPosts } from '../mapping.js';
import { Attachments, uploadFile } from './attachments.jsx';
import { Avatar, Empty, Icon, Lbl, Loading, Modal, PH, PageTabs, Panel } from './components.jsx';
import { Button, Checkbox, DataTable, Field, Pill, RadioGroup } from './ds/fgcp-ds.jsx';
import { BoardAdmin, CATEGORY } from './board-admin.jsx';

// board.jsx — 게시판·공지(계획 문서 5.2 2-4). 보이는 게시판·글과 할 수 있는 일(쓰기·관리)은 서버가 게시판 규칙(읽기·쓰기·관리 × 전사·부서·역할)으로
// 정한다 — 화면은 받은 것만 그린다. 게시판은 id로 고른다(이름이 같아도 섞이지 않게 — D16). 목록·검색·쪽 나누기는 서버.
// 탭: 게시판 · 게시판 관리(actions.manage — 그룹웨어 관리자). 글 쓰기·고치기·지우기·변경 이력(2-4b)도 서버 actions로만 보인다.
// 본문은 서버가 허용 목록으로 정제한 HTML(body_html)을 그대로 보인다 — 없으면 지난 글이라 글자 그대로. 편집기는 따로 싣는다(post-editor.jsx).

const { useState: uS, useEffect: uE, useCallback: uCb } = React;
const PAGE = 20;
const PostEditor = React.lazy(() => import('./post-editor.jsx'));
const when = iso => T.format(iso, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

/** 본문 — 서버가 정제한 HTML(없으면 지난 글: 글자 그대로) */
function PostBody({ html, text }) {
  return html
    ? <div className="post-body" data-post-body dangerouslySetInnerHTML={{ __html: html }} />
    : <p className="post-body" data-post-body style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{text}</p>;
}

/** 글 쓰기·고치기 창 — 첨부는 올린 목록을 저장할 때 fileIds로(서버가 같은 트랜잭션에서 붙이고 뗀다). 고정·중요는 관리 권한일 때만 */
function PostEditModal({ board, post, files, onClose, onSaved, onToast }) {
  const editing = Boolean(post);
  const [title, setTitle] = uS(post?.title ?? '');
  const [html, setHtml] = uS(post?.body_html ?? (post?.body ? post.body.split('\n').map(l => `<p>${l.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c])}</p>`).join('') : ''));
  const [fileIds, setFileIds] = uS((files ?? []).map(f => f.id));
  const [pinned, setPinned] = uS(post?.is_pinned ?? false);
  const [important, setImportant] = uS(post?.importance === 'high');
  const [busy, setBusy] = uS(false);
  const docs = board.category === 'docs';
  const [categoryId, setCategoryId] = uS(post?.category_id ?? '');
  const [versions, setVersions] = uS([]);   // 문서함 새 판 — [{of, file}]
  const canPin = editing ? post.actions?.pin : board.actions?.manage;
  const addVersion = (of, picked) => {
    if (!picked) return;
    setBusy(true);
    uploadFile(picked, 'boards')
      .then(file => setVersions(v => [...v.filter(x => x.of !== of), { of, file }]))
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  const save = () => {
    setBusy(true);
    // 새 판을 올린 첨부는 보내는 목록에서 새 파일로 바뀐다(서버는 새 판을 먼저 반영하고 목록을 그 뒤의 지금 판 기준으로 본다)
    const sendIds = fileIds.map(f => versions.find(v => v.of === f)?.file.id ?? f);
    const body = { title: title.trim(), bodyHtml: html, fileIds: sendIds, ...(canPin ? { isPinned: pinned, importance: important ? 'high' : 'normal' } : {}),
      ...(docs ? { categoryId: categoryId || null } : {}), ...(versions.length ? { newVersions: versions.map(v => ({ of: v.of, fileId: v.file.id })) } : {}) };
    (editing ? api.patch('/api/v1/posts/' + post.id, body) : api.post('/api/v1/posts', { ...body, boardId: board.id }))
      .then(r => { onToast(editing ? '글을 고쳤습니다' : '글을 올렸습니다'); onSaved(r.id ?? post.id); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={760} title={editing ? '글 고치기' : `새 글 — ${board.name}`} icon="megaphone" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        <Button variant="primary" size="sm" disabled={busy || !title.trim()} onClick={save}>{editing ? '저장' : '올리기'}</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-post-edit>
        <Field label="제목" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
        {docs && (board.categories?.length > 0) && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>문서 분류</Lbl>
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} aria-label="문서 분류"
              style={{ padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, maxWidth: 260 }}>
              <option value="">분류 없음</option>
              {board.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></label>
        )}
        <div>
          <Lbl style={{ marginBottom: 6 }}>본문</Lbl>
          <React.Suspense fallback={<Loading title="편집기를 불러오는 중" />}>
            <PostEditor html={html} onChange={setHtml} />
          </React.Suspense>
        </div>
        <Attachments scope="boards" canUpload initialFiles={files ?? []} onChange={list => setFileIds(list.map(f => f.id))} />
        {docs && editing && (files ?? []).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-new-versions>
            <Lbl>새 판 올리기 (이전 판은 남아 내려받을 수 있습니다)</Lbl>
            {files.filter(f => fileIds.includes(f.id)).map(f => {
              const v = versions.find(x => x.of === f.id);
              return (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }} data-version-of={f.id}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name} <span className="mono" style={{ color: 'var(--ink-3)' }}>{f.version ?? 1}판</span></span>
                  {v && <span style={{ color: 'var(--acc)' }}>→ {v.file.original_name} ({(f.version ?? 1) + 1}판)</span>}
                  <label className="link" style={{ cursor: 'pointer' }}>{v ? '다시 고르기' : '새 판 파일'}
                    <input type="file" style={{ display: 'none' }} aria-label={`${f.original_name} 새 판`} onChange={e => { addVersion(f.id, e.target.files?.[0]); e.target.value = ''; }} /></label>
                  {v && <button type="button" className="link" onClick={() => setVersions(x => x.filter(y => y.of !== f.id))}>취소</button>}
                </div>);
            })}
          </div>
        )}
        {canPin && (
          <div style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
            <Checkbox label="고정" checked={pinned} onChange={setPinned} />
            <Checkbox label="중요" checked={important} onChange={setImportant} />
          </div>
        )}
        {editing && <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>제목·본문을 바꾸면 이전 내용이 변경 이력에 남습니다. 뗀 첨부도 기록으로 남습니다.</div>}
      </div>
    </Modal>
  );
}

/** 변경 이력 — 고치기 전 판들(최신 순) */
function Revisions({ id, onClose, onToast }) {
  const [list, setList] = uS(null);
  uE(() => {
    api.get(`/api/v1/posts/${id}/revisions`).then(r => setList(r.revisions)).catch(e => { setList([]); onToast(e.message); });
  }, [id, onToast]);
  return (
    <Modal width={700} title="변경 이력" icon="clock" onClose={onClose} footer={<Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>}>
      {!list ? <Loading title="불러오는 중" /> : !list.length ? <Empty icon="clock" title="고친 적이 없습니다" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-revisions>
          {list.map(r => (
            <div key={r.revision} data-revision={r.revision} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', padding: '10px 12px' }}>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>{r.revision}판 · {r.edited_by_name}님이 {when(r.edited_at)}에 고치기 전 내용</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{r.title}</div>
              <PostBody html={r.body_html} text={r.body} />
            </div>))}
        </div>
      )}
    </Modal>
  );
}

/** 읽은·안 읽은 사람(작성자·게시판 관리자) — 안 읽은 사람은 지금 이 게시판을 읽을 수 있는 사람 중(서버가 정한 범위) */
function Readers({ id, onClose, onToast }) {
  const [data, setData] = uS(null);
  uE(() => {
    api.get(`/api/v1/posts/${id}/readers`).then(setData).catch(e => { onToast(e.message); onClose(); });
  }, [id, onToast, onClose]);
  const col = (title, list, withTime) => (
    <div style={{ flex: '1 1 240px', minWidth: 0 }}>
      <Lbl style={{ marginBottom: 6 }}>{title} <span className="mono">{list.length}</span></Lbl>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: '46vh', overflowY: 'auto' }}>
        {list.map(p => <div key={p.id} style={{ fontSize: 12.5 }}>{p.name}<span style={{ color: 'var(--ink-3)' }}>{p.department ? ` · ${p.department}` : ''}{withTime ? ` · ${when(p.read_at)}` : ''}</span></div>)}
      </div>
    </div>);
  return (
    <Modal width={640} title="읽은 사람" icon="check-circle" onClose={onClose} footer={<Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>}>
      {!data ? <Loading title="불러오는 중" /> : (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }} data-readers={data.read.length} data-unread={data.unread.length}>
          {col('읽음', data.read, true)}{col('안 읽음', data.unread, false)}
        </div>)}
    </Modal>
  );
}

/** 글 상세 — 본문·조회 수·할 수 있는 일은 열 때 서버에서(못 받으면 알리고 목록으로) */
function PostDetail({ id, boards, onBack, onToast, onChanged }) {
  const [full, setFull] = uS(null);
  const [files, setFiles] = uS(null);
  const [mode, setMode] = uS(null);   // 'edit' | 'delete' | 'revisions'
  const [reason, setReason] = uS('');
  const [rev, setRev] = uS(0);
  const outer = React.useRef({ onToast, onBack });
  outer.current = { onToast, onBack };
  uE(() => {
    let current = true;
    api.get('/api/v1/posts/' + id)
      .then(r => { if (current) setFull({ ...mapPosts([r.post])[0], raw: r.post, body: r.post.body, html: r.post.body_html, views: r.post.read_count, active: r.post.board_active }); })
      .catch(e => { outer.current.onToast(e.message); outer.current.onBack(); });
    api.get('/api/v1/files?postId=' + id).then(r => { if (current) setFiles(r.files); }).catch(() => { if (current) setFiles([]); });
    return () => { current = false; };
  }, [id, rev]);
  const a = full?.raw.actions ?? {};
  const closeMode = uCb(() => setMode(null), []);
  const remove = () => api.post(`/api/v1/posts/${id}/delete`, { reason: reason.trim() || null })
    .then(() => { onToast('글을 지웠습니다'); onChanged?.(); onBack(); })
    .catch(e => onToast(e.message));
  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#/board/${id}`;
    (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject(new Error('이 브라우저는 복사를 지원하지 않습니다')))
      .then(() => onToast('글 링크를 복사했습니다'))
      .catch(e => onToast('링크를 복사하지 못했습니다 — ' + e.message));
  };
  if (!full) return <div className="body"><Panel><Loading title="불러오는 중" /></Panel></div>;
  return (
    <React.Fragment>
      <div className="head"><button className="link" onClick={onBack}><Icon name="chevron-left" size={13} />{full.board}</button></div>
      <div className="body">
        <Panel>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)' }} data-post={id}>
            <div style={{ display: 'flex', gap: 7, marginBottom: 9, flexWrap: 'wrap' }}>
              {full.important && <Pill tone="crit" dot={false}>중요</Pill>}
              {full.pinned && <Pill tone="accent" dot={false}>고정</Pill>}
              <Pill tone="neutral" dot={false}>{full.board}</Pill>
              {full.category && <Pill tone="info" dot={false}>{full.category}</Pill>}
              {!full.active && <Pill tone="neutral" dot={false}>사용 중지된 게시판</Pill>}
              {full.raw.status === 'deleted' && <Pill tone="crit" dot={false}>지운 글</Pill>}
            </div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: '-.03em' }}>{full.title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <Avatar name={full.author} size={26} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{full.author} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>· {full.dept}</span></div>
                <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{full.at} · {a.readers
                  ? <button className="link" onClick={() => setMode('readers')} data-read-count={full.views ?? 0}>조회 {full.views ?? 0}</button> : `조회 ${full.views ?? 0}`}
                  {full.raw.revision_count > 0 && <React.Fragment> · <button className="link" onClick={() => setMode('revisions')} data-revision-count={full.raw.revision_count}>고침 {full.raw.revision_count}회</button></React.Fragment>}</div>
              </div>
            </div>
          </div>
          <div style={{ padding: 20 }}>
            {full.raw.status === 'deleted' && <div style={{ fontSize: 12, color: 'var(--crit)', marginBottom: 12 }} data-deleted-note>
              {full.raw.deleted_by_name}님이 {when(full.raw.deleted_at)}에 지운 글입니다{full.raw.delete_reason ? ` — ${full.raw.delete_reason}` : ''}. 게시판 관리자에게만 보입니다.</div>}
            <PostBody html={full.html} text={full.body} />
            <div style={{ marginTop: 18 }}>
              {files && <Attachments key={rev} target={{ postId: id }} scope="boards" />}
            </div>
          </div>
          {mode === 'delete' && (
            <div className="noprint" style={{ padding: '0 20px 12px' }}>
              <Field label={a.deleteReasonRequired ? '지우는 사유 (필수 — 작성자에게 남습니다)' : '지우는 사유 (선택)'} value={reason}
                onChange={e => setReason(e.target.value)} maxLength={200} aria-label="지우는 사유" />
            </div>
          )}
          <div className="noprint" style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {mode === 'delete' ? <React.Fragment>
              <Button variant="secondary" size="sm" onClick={() => { setMode(null); setReason(''); }}>돌아가기</Button>
              <Button variant="danger" size="sm" disabled={a.deleteReasonRequired && !reason.trim()} onClick={remove}>지우기</Button>
            </React.Fragment> : <React.Fragment>
              {a.delete && <Button variant="danger" size="sm" onClick={() => setMode('delete')}>지우기</Button>}
              {a.edit && <Button variant="secondary" size="sm" disabled={!files} onClick={() => setMode('edit')}>고치기</Button>}
              <Button variant="secondary" size="sm" onClick={copyLink}>링크 복사</Button>
              <Button variant="secondary" size="sm" onClick={() => window.print()}>인쇄</Button>
            </React.Fragment>}
          </div>
        </Panel>
      </div>
      {mode === 'edit' && <PostEditModal board={boards?.find(b => b.id === full.raw.board_id) ?? { id: full.raw.board_id, name: full.board, category: full.raw.board_category }} post={full.raw} files={files} onToast={onToast}
        onClose={() => setMode(null)} onSaved={() => { setMode(null); setRev(x => x + 1); onChanged?.(); }} />}
      {mode === 'revisions' && <Revisions id={id} onToast={onToast} onClose={() => setMode(null)} />}
      {mode === 'readers' && <Readers id={id} onToast={onToast} onClose={closeMode} />}
    </React.Fragment>
  );
}

/** 한 게시판의 글 목록 — 서버의 쪽·검색(제목·본문·작성자·부서), 쓸 수 있으면 '새 글' */
function PostList({ board, onOpen, onToast, onWrite, rev }) {
  const [input, setInput] = uS('');
  const [q, setQ] = uS('');
  const [page, setPage] = uS(1);
  const [data, setData] = uS(null);
  const [cat, setCat] = uS('');   // 문서함 분류 거르기
  uE(() => { setPage(1); setInput(''); setQ(''); setCat(''); }, [board.id]);
  uE(() => {
    let current = true;
    setData(null);
    api.get(`/api/v1/posts?boardId=${board.id}&page=${page}&pageSize=${PAGE}${q ? '&q=' + encodeURIComponent(q) : ''}${cat ? '&categoryId=' + cat : ''}`)
      .then(r => { if (current) setData({ ...r, posts: mapPosts(r.posts) }); })
      .catch(e => { if (current) { setData({ posts: [], total: 0, page: 1, pageSize: PAGE }); onToast(e.message); } });
    return () => { current = false; };
  }, [board.id, page, q, cat, onToast, rev]);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  return (
    <Panel scroll>
      <PH title={board.name} sub={data ? `${data.total}건${q ? ` · '${q}' 검색` : ''}` : ''}
        right={<form onSubmit={e => { e.preventDefault(); setPage(1); setQ(input.trim()); }} style={{ display: 'flex', gap: 6 }} data-post-search>
          <input value={input} onChange={e => setInput(e.target.value)} placeholder="제목·본문·작성자·부서" maxLength={100} aria-label="게시글 검색"
            style={{ padding: '6px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, width: 180 }} />
          <Button variant="secondary" size="sm" type="submit">검색</Button>
          {board.actions?.write && <Button variant="primary" size="sm" type="button" onClick={onWrite}>새 글</Button>}
        </form>} />
      {board.category === 'docs' && board.categories?.length > 0 && (
        <div style={{ padding: '0 16px 10px' }} data-categories>
          <RadioGroup orientation="horizontal" aria-label="분류" value={cat} onChange={v => { setCat(v); setPage(1); }}
            options={[{ value: '', label: '전체' }, ...board.categories.map(c => ({ value: c.id, label: <span data-category={c.id}>{c.name}<span className="mono" style={{ color: 'var(--ink-3)', marginLeft: 4 }}>{c.post_count}</span></span> }))]} />
        </div>
      )}
      {!data ? <Loading title="불러오는 중" /> : !data.posts.length ? <Empty icon="megaphone" title={q ? '찾는 글이 없습니다' : '글이 없습니다'} /> : (
        <div data-post-list={board.id}>
          <DataTable columns={[
            { key: 'title', header: '제목', strong: true, render: p => (
              <button onClick={() => onOpen(p.id)} data-post-row={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, textAlign: 'left' }}>
                {p.pinned && <Icon name="megaphone" size={13} color="var(--acc)" />}
                {p.important && <Pill tone="crit" dot={false}>중요</Pill>}
                <span style={{ fontWeight: p.read ? 400 : 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</span>
                {!p.read && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--acc-graph)', flex: 'none' }} />}
              </button>) },
            { key: 'author', header: '작성자', width: 100, render: p => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><Avatar name={p.author} size={20} />{p.author}</span> },
            { key: 'dept', header: '부서', width: 104, render: p => <span style={{ fontSize: 11.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{p.dept}</span> },
            ...(board.category === 'docs' ? [{ key: 'category', header: '분류', width: 90, render: p => <span style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{p.category || '—'}</span> }] : []),
            { key: 'at', header: '등록', mono: true, align: 'right', width: 62 },
            { key: 'views', header: '조회', mono: true, align: 'right', width: 52 },
          ]} rows={data.posts} />
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 10, alignItems: 'center' }}>
              <button className="ico" aria-label="이전 쪽" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icon name="chevron-left" size={14} /></button>
              <span className="mono" style={{ fontSize: 11.5 }}>{page} / {pages}</span>
              <button className="ico" aria-label="다음 쪽" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><Icon name="chevron-right" size={14} /></button>
            </div>)}
        </div>
      )}
    </Panel>
  );
}

function Board({ onToast, deepPost }) {
  const [tab, setTab] = uS('list');
  const [boards, setBoards] = uS(null);
  const [manage, setManage] = uS(false);
  const [boardId, setBoardId] = uS(null);
  const [postId, setPostId] = uS(null);   // 연 글 — 목록에서 고르거나 검색·알림 링크(#/board/<id>)로 들어온다
  const [writing, setWriting] = uS(false);
  const [rev, setRev] = uS(0);            // 글을 쓰거나 고치거나 지우면 목록·칩 글 수를 다시 받는다
  const loadBoards = uCb(() => api.get('/api/v1/boards')
    .then(r => {
      setBoards(r.boards); setManage(r.actions?.manage === true);
      setBoardId(id => (id && r.boards.some(b => b.id === id) ? id : r.boards[0]?.id ?? null));
    })
    .catch(e => { setBoards([]); onToast('게시판 목록을 받지 못했습니다 — ' + e.message); }), [onToast]);
  uE(() => { void loadBoards(); }, [loadBoards]);
  uE(() => { if (deepPost?.id) { setTab('list'); setPostId(deepPost.id); } }, [deepPost]);
  const board = boards?.find(b => b.id === boardId);

  const changed = () => { setRev(x => x + 1); void loadBoards(); };
  if (postId) return <PostDetail id={postId} boards={boards} onBack={() => setPostId(null)} onToast={onToast} onChanged={changed} />;
  return (
    <React.Fragment>
      <div className="head">
        <h1>게시판 · 공지</h1>
        <div className="meta">{boards && <Pill tone="neutral" dot={false}><span className="mono">게시판 {boards.length}개</span></Pill>}</div>
      </div>
      {manage && (
        <PageTabs value={tab} onChange={setTab} items={[{ value: 'list', label: '게시판' }, { value: 'admin', label: '게시판 관리' }]} />
      )}
      <div className="body" style={manage ? { paddingTop: 14 } : undefined}>
        {tab === 'admin' && manage ? <BoardAdmin onToast={onToast} onChanged={loadBoards} /> : !boards ? <Panel><Loading title="불러오는 중" /></Panel>
          : !boards.length ? <Panel><Empty icon="megaphone" title="볼 수 있는 게시판이 없습니다" /></Panel> : (
          <React.Fragment>
            <RadioGroup orientation="horizontal" data-boards aria-label="게시판" value={boardId} onChange={setBoardId}
              options={boards.map(b => ({ value: b.id, label: <span data-board={b.id} title={b.description ?? CATEGORY[b.category]}>{b.name}<span className="mono" style={{ color: 'var(--ink-3)', marginLeft: 4 }}>{b.post_count}</span></span> }))} />
            {board && <PostList board={board} onOpen={setPostId} onToast={onToast} onWrite={() => setWriting(true)} rev={rev} />}
          </React.Fragment>
        )}
      </div>
      {writing && board && <PostEditModal board={board} onToast={onToast} onClose={() => setWriting(false)}
        onSaved={id => { setWriting(false); changed(); setPostId(id); }} />}
    </React.Fragment>
  );
}

export { Board };
